import React, { useState, useEffect, useRef } from 'react';
import { adminGetTestimonials, adminAddTestimonial, adminUpdateTestimonial, adminDeleteTestimonial } from '../../api/admin';
import AdminLayout from './AdminLayout';
import './AdminTestimonials.css';

const EMPTY_FORM = {
  id: null,
  client_name: '',
  designation: '',
  company: '',
  message: '',
  rating: 5,
  sort_order: 0,
  is_active: 1,
  client_photo: '',
  gallery_images: []
};

function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [clientPhotoFile, setClientPhotoFile] = useState(null);
  const [clientPhotoPreview, setClientPhotoPreview] = useState('');
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const fileInputRef = useRef();
  const galleryInputRef = useRef();

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    setLoading(true);
    const res = await adminGetTestimonials();
    if (res.success) {
      // Ensure gallery_images is always an array
      const data = (res.data || []).map(item => ({
        ...item,
        gallery_images: Array.isArray(item.gallery_images) 
          ? item.gallery_images 
          : item.gallery_images ? JSON.parse(item.gallery_images) : []
      }));
      setItems(data);
    }
    setLoading(false);
  };

  const openAdd = () => {
    setForm({ ...EMPTY_FORM, gallery_images: [] });
    setEditing(null);
    setClientPhotoFile(null);
    setClientPhotoPreview('');
    setGalleryFiles([]);
    setGalleryPreviews([]);
    setShowForm(true);
    setMsg('');
  };

  const openEdit = (item) => {
    const galleryImages = Array.isArray(item.gallery_images) 
      ? item.gallery_images 
      : item.gallery_images ? JSON.parse(item.gallery_images) : [];
    setForm({ ...item, gallery_images: galleryImages });
    setEditing(item);
    setClientPhotoFile(null);
    setClientPhotoPreview(item.client_photo || '');
    setGalleryFiles([]);
    setGalleryPreviews(galleryImages || []);
    setShowForm(true);
    setMsg('');
  };

  const closeForm = () => {
    setShowForm(false);
    setMsg('');
  };

  const handleClientPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setClientPhotoFile(file);
      setClientPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setGalleryFiles([...galleryFiles, ...files]);
      const previews = files.map(file => URL.createObjectURL(file));
      setGalleryPreviews([...galleryPreviews, ...previews]);
    }
  };

  const removeGalleryImage = (index) => {
    setGalleryFiles(galleryFiles.filter((_, i) => i !== index));
    setGalleryPreviews(galleryPreviews.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!form.client_name.trim()) {
      setMsg('Client name is required');
      return;
    }
    if (!form.message.trim()) {
      setMsg('Review message is required');
      return;
    }

    setSaving(true);
    setMsg('');

    try {
      const formData = new FormData();
      formData.append('client_name', form.client_name);
      formData.append('designation', form.designation || '');
      formData.append('company', form.company || '');
      formData.append('message', form.message);
      formData.append('rating', form.rating);
      formData.append('sort_order', form.sort_order || 0);
      formData.append('is_active', form.is_active || 1);

      if (clientPhotoFile) {
        formData.append('client_photo', clientPhotoFile);
      }

      if (galleryFiles.length > 0) {
        galleryFiles.forEach((file) => {
          formData.append('gallery_images[]', file);
        });
      }

      let res;
      if (editing) {
        formData.append('id', editing.id);
        res = await adminUpdateTestimonial(formData);
      } else {
        res = await adminAddTestimonial(formData);
      }

      if (res.success) {
        setMsg('✅ Testimonial saved successfully!');
        setForm({ ...EMPTY_FORM, gallery_images: [] });
        setClientPhotoFile(null);
        setClientPhotoPreview('');
        setGalleryFiles([]);
        setGalleryPreviews([]);
        setEditing(null);
        setShowForm(false);
        fetchItems();
      } else {
        setMsg('❌ Error: ' + res.message);
      }
    } catch (error) {
      setMsg('❌ Network error. Please try again.');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    const res = await adminDeleteTestimonial(id);
    if (res.success) fetchItems();
  };

  if (loading) {
    return (
      <AdminLayout title="Testimonials">
        <div className="adm-loading">Loading...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Testimonials">
      <div className="adm-page-header">
        <h2>Manage Testimonials</h2>
        <button className="adm-btn-primary" onClick={openAdd}>+ Add Testimonial</button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="adm-modal-overlay" onClick={closeForm}>
          <div className="adm-modal" onClick={e => e.stopPropagation()}>
            <div className="adm-modal__header">
              <h3>{editing ? 'Edit Testimonial' : 'Add New Testimonial'}</h3>
              <button onClick={closeForm}>✕</button>
            </div>
            <div className="adm-modal__body">
              {msg && <div className={`adm-msg ${msg.includes('Error') ? 'adm-msg--error' : 'adm-msg--success'}`}>{msg}</div>}

              <div className="adm-form-field">
                <label>Client Name *</label>
                <input
                  type="text"
                  value={form.client_name}
                  onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                  placeholder="e.g. Rajesh Kumar"
                />
              </div>

              <div className="adm-form-row">
                <div className="adm-form-field">
                  <label>Rating</label>
                  <select
                    value={form.rating}
                    onChange={(e) => setForm({ ...form, rating: parseInt(e.target.value) })}
                  >
                    {[5, 4, 3, 2, 1].map(r => (
                      <option key={r} value={r}>{'★'.repeat(r)}{'☆'.repeat(5-r)} ({r})</option>
                    ))}
                  </select>
                </div>
                <div className="adm-form-field">
                  <label>Sort Order</label>
                  <input
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="adm-form-row">
                <div className="adm-form-field">
                  <label>Designation</label>
                  <input
                    type="text"
                    value={form.designation}
                    onChange={(e) => setForm({ ...form, designation: e.target.value })}
                    placeholder="e.g. Property Owner"
                  />
                </div>
                <div className="adm-form-field">
                  <label>Company</label>
                  <input
                    type="text"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    placeholder="e.g. ABC Pvt Ltd"
                  />
                </div>
              </div>

              <div className="adm-form-field">
                <label>Client Photo</label>
                <div className="adm-upload-box" onClick={() => fileInputRef.current?.click()}>
                  {clientPhotoPreview ? (
                    <img src={clientPhotoPreview} alt="Client" className="adm-upload-preview" />
                  ) : (
                    <div className="adm-upload-placeholder">📷 Click to upload client photo</div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleClientPhotoChange}
                />
                <small style={{ color: '#888' }}>JPG, PNG, WEBP - Max 2MB</small>
              </div>

              <div className="adm-form-field">
                <label>Gallery Images (Multiple)</label>
                <div className="adm-upload-box" onClick={() => galleryInputRef.current?.click()}>
                  {galleryPreviews.length > 0 ? (
                    <div className="adm-gallery-previews">
                      {galleryPreviews.map((preview, index) => (
                        <div key={index} className="adm-gallery-preview-item">
                          <img src={preview} alt={`Gallery ${index}`} />
                          <button type="button" onClick={(e) => { e.stopPropagation(); removeGalleryImage(index); }}>✕</button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="adm-upload-placeholder">📷 Click to upload multiple images</div>
                  )}
                </div>
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleGalleryChange}
                />
                <small style={{ color: '#888' }}>JPG, PNG, WEBP - Max 5MB each</small>
              </div>

              <div className="adm-form-field">
                <label>Review Message *</label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={4}
                  placeholder="What did the client say..."
                />
              </div>

              <div className="adm-form-field">
                <label>Status</label>
                <select
                  value={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: parseInt(e.target.value) })}
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>

              <div className="adm-modal__footer">
                <button className="adm-btn-secondary" onClick={closeForm}>Cancel</button>
                <button className="adm-btn-primary" onClick={handleSubmit} disabled={saving}>
                  {saving ? 'Saving...' : editing ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="adm-section">
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Photo</th>
                <th>Name</th>
                <th>Rating</th>
                <th>Message</th>
                <th>Gallery</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>No testimonials added yet.</td></tr>
              )}
              {items.map(item => (
                <tr key={item.id}>
                  <td>
                    {item.client_photo ? (
                      <img src={item.client_photo} alt={item.client_name} className="adm-thumb" style={{ width: 50, height: 50, borderRadius: '50%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: 50, height: 50, borderRadius: '50%', background: '#e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>📷</div>
                    )}
                  </td>
                  <td>
                    <strong>{item.client_name}</strong>
                    {item.designation && <div style={{ fontSize: 12, color: '#888' }}>{item.designation}</div>}
                  </td>
                  <td>{'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}</td>
                  <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.message}
                  </td>
                  <td>
                    {item.gallery_images && item.gallery_images.length > 0 ? (
                      <span>📸 {item.gallery_images.length}</span>
                    ) : (
                      <span style={{ color: '#888' }}>—</span>
                    )}
                  </td>
                  <td>
                    <span className={`adm-status ${item.is_active ? 'adm-status--active' : 'adm-status--inactive'}`}>
                      {item.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="adm-actions">
                      <button className="adm-btn-edit" onClick={() => openEdit(item)}>✏️</button>
                      <button className="adm-btn-delete" onClick={() => handleDelete(item.id)}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminTestimonials;